/**
 * The single client for the backend.
 *
 * Unwraps the response envelope so callers get `data` directly, and throws a
 * typed `ApiRequestError` on failure — so every component handles errors the
 * same way and nothing has to remember to check `ok`.
 */

import type { ApiError, Envelope } from '@/core/types/api.types'

const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')
const API_PREFIX = '/api/v1'

export class ApiRequestError extends Error {
  readonly code: string
  readonly status: number
  readonly retryable: boolean
  readonly details: ApiError['details']
  readonly requestId: string | null

  constructor(error: ApiError, status: number, requestId: string | null) {
    super(error.message)
    this.name = 'ApiRequestError'
    this.code = error.code
    this.status = status
    this.retryable = error.retryable
    this.details = error.details
    this.requestId = requestId
  }

  /** Field-level messages, keyed by input path, for mapping onto form inputs. */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {}
    for (const f of this.details?.fields ?? []) out[f.path] = f.message
    return out
  }
}

/**
 * How the client obtains a bearer token.
 *
 * Pluggable so the auth provider (Supabase today, anything later) is injected
 * once at startup rather than imported here — which would make this module
 * depend on a specific auth vendor.
 */
type TokenProvider = () => Promise<string | null>

let getToken: TokenProvider = async () => null
let onUnauthorized: (() => void) | null = null

export function configureApiAuth(provider: TokenProvider, onAuthFailure?: () => void) {
  getToken = provider
  onUnauthorized = onAuthFailure ?? null
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'
  body?: unknown
  /** A multipart body. Sent as-is; the browser sets the boundary header. */
  form?: FormData
  query?: Record<string, string | number | boolean | undefined | null>
  signal?: AbortSignal
  /** Internal: prevents an infinite retry loop on repeated 401s. */
  _isRetry?: boolean
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${API_BASE}${API_PREFIX}${path}`
  if (!query) return url

  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value))
    }
  }
  const qs = params.toString()
  return qs ? `${url}?${qs}` : url
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, form, query, signal } = options

  const headers: Record<string, string> = { Accept: 'application/json' }
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  const token = await getToken()
  if (token) headers.Authorization = `Bearer ${token}`

  let response: Response
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: form ?? (body === undefined ? undefined : JSON.stringify(body)),
      signal,
    })
  } catch (cause) {
    if (signal?.aborted) throw cause
    // A network-level failure has no envelope to unwrap, so synthesise one that
    // matches the shape everything downstream expects.
    throw new ApiRequestError(
      {
        code: 'network_error',
        message:
          'Could not reach the API. Check that the backend is running on port 8000.',
        retryable: true,
      },
      0,
      null,
    )
  }

  const requestId = response.headers.get('x-request-id')

  // 204 and other empty bodies are legitimate; don't try to parse them.
  const text = await response.text()
  let payload: Envelope<T> | null = null
  if (text) {
    try {
      payload = JSON.parse(text) as Envelope<T>
    } catch {
      payload = null
    }
  }

  if (!response.ok) {
    // One retry on 401: the session may simply have expired, and refreshing
    // then replaying is invisible to the user.
    if (response.status === 401 && !options._isRetry) {
      const refreshed = await getToken()
      if (refreshed) {
        return request<T>(path, { ...options, _isRetry: true })
      }
      onUnauthorized?.()
    }

    throw new ApiRequestError(
      payload?.error ?? {
        code: `http_${response.status}`,
        message: response.statusText || 'The request failed.',
        retryable: response.status >= 500,
      },
      response.status,
      requestId,
    )
  }

  if (payload && payload.ok === false && payload.error) {
    throw new ApiRequestError(payload.error, response.status, requestId)
  }

  return (payload?.data ?? null) as T
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    request<T>(path, { method: 'GET', query, signal }),
  post: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'POST', body, signal }),
  upload: <T>(path: string, form: FormData, signal?: AbortSignal) =>
    request<T>(path, { method: 'POST', form, signal }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}
